import { getTemplateName } from "helpers/templates";
import { format, fromUnixTime } from "date-fns";
import CONSTANTS from "core/constants";

export function formatPaymentData(rawPayments) {
  if (!rawPayments || !rawPayments.rows) {
    return [];
  }
  return rawPayments.rows.map((payment) => {
    // Add compatibility with old api
    const payedAmount = payment.payedAmount ?? payment.amount;
    return {
      createdAt: format(fromUnixTime(payment.createdAt), CONSTANTS.DATE_FORMAT.YEAR_NAME_DATE_FORMAT),
      nationalIdentity: payment.nationalIdentity,
      patientFirstName: payment.patientFirstName,
      patientLastName: payment.patientLastName,
      templateName: payment.templateName,
      amount: payment.amount
        ? `${payment.amount / 100} ${payment.currency}`
        : `${payment.amount} ${payment.currency}`,
      payedAmount: payedAmount
        ? `${payedAmount / 100} ${payment.currency}`
        : `${payedAmount} ${payment.currency}`,
      payedAmountValue: payedAmount,
      paymentMethod: payment.type,
      paymentId: payment.paymentId,
      amountValue: payment.amount,
      psychologistFirstName: payment.psychologistFirstName,
      psychologistLastName: payment.psychologistLastName,
      slotId: payment.slotId,
      status: payment.status,
      currency: payment.currency,
      provider: payment.provider,
      countryId: payment.countryId,
      isRefundable: payment.isRefundable,
      isPartiallyRefundable: payment.isPartiallyRefundable,
    };
  });
}

export function getPaymentTableColumns(t) {
  // id is used for sorting tables, this value should be
  // same as the object key for the specific column..
  return [
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: t("TIME"),
    },
    {
      id: "nationalIdentity",
      numeric: false,
      disablePadding: false,
      label: t("NATIONAL_ID"),
    },
    {
      id: "slotId",
      numeric: false,
      disablePadding: false,
      label: t("BOOKING_ID"),
    },
    {
      id: "patientFirstName",
      numeric: false,
      disablePadding: false,
      label: t("PATIENT_NAME"),
    },
    {
      id: "patientLastName",
      numeric: false,
      disablePadding: false,
      label: t("PATIENT_LASTNAME"),
    },
    {
      id: "templateName",
      numeric: false,
      disablePadding: false,
      label: t("TYPE_OF_VISIT"),
    },
    {
      id: "amount",
      numeric: false,
      disablePadding: false,
      label: t("AMOUNT"),
    },
    {
      id: "payedAmount",
      numeric: false,
      disablePadding: false,
      label: t("AMOUNT_PAID"),
    },
    {
      id: "paymentMethod",
      numeric: false,
      disablePadding: false,
      label: t("PAYMENT_METHOD"),
    },
    {
      id: "paymentId",
      numeric: false,
      disablePadding: false,
      label: t("PAYMENT_ID"),
    },
    {
      id: "status",
      numeric: false,
      disablePadding: false,
      label: t("STATUS"),
    },
    {
      id: "psychologistFirstName",
      numeric: false,
      disablePadding: false,
      label: t("PSYCHOLOGIST_NAME"),
    },
    {
      id: "psychologistLastName",
      numeric: false,
      disablePadding: false,
      label: t("PSYCHOLOGIST_LASTNAME"),
    },
  ];
}

export function formatPaymentTableData(data, t, currentCountryId) {
  return data
    .filter((d) => d.countryId === currentCountryId)
    .map((d) => {
      const {
        createdAt,
        nationalIdentity,
        slotId,
        patientFirstName,
        patientLastName,
        templateName,
        amount,
        payedAmount,
        paymentMethod,
        paymentId,
        amountValue,
        psychologistFirstName,
        psychologistLastName,
        status,
        provider,
        countryId,
      } = d;
      return {
        id: slotId + nationalIdentity,
        createdAt,
        nationalIdentity,
        slotId,
        patientFirstName,
        patientLastName,
        templateName: getTemplateName(templateName, t),
        amount,
        payedAmount,
        paymentMethod,
        paymentId,
        amountValue,
        status,
        psychologistFirstName,
        psychologistLastName,
        provider,
        countryId,
      };
    });
}

export function getPaymentTableFilters(t, values) {
  const filters = [
    {
      id: "dateFrom",
      type: "date",
      label: t("FROM"),
    },
    {
      id: "dateTo",
      type: "date",
      label: t("TO"),
    },
    {
      id: "userId",
      type: "text",
      label: t("USER_ID"),
    },
    {
      id: "nationalIdentity",
      type: "text",
      label: t("NATIONAL_ID"),
    },
    {
      id: "slotId",
      type: "text",
      label: t("BOOKING_ID"),
    },
    {
      id: "patientFirstName",
      type: "text",
      label: t("PATIENT_NAME"),
    },
    {
      id: "patientLastName",
      type: "text",
      label: t("PATIENT_LASTNAME"),
    },
    {
      id: "psychologistFirstName",
      type: "text",
      label: t("PSYCHOLOGIST_NAME"),
    },
    {
      id: "psychologistLastName",
      type: "text",
      label: t("PSYCHOLOGIST_LASTNAME"),
    },
  ];

  return filters.map((filter) => {
    const value = values[filter.id];
    const updatedFilter = filter;
    if (value !== undefined) {
      updatedFilter.value = value;
    }

    return updatedFilter;
  });
}

export function getPaymentTableSensitiveFields() {
  return [
    "patientFirstName",
    "patientLastName",
    "nationalIdentity",
    "psychologistFirstName",
    "psychologistLastName",
  ];
}
